import {
  IBooleanV3TimelineItemDto,
  IFullV3TimelineItemDto,
  IGapV3TimelineItemDto,
  IOverlapV3TimelineItemDto,
} from '@data-access/bulk-operations-api';

import { getEarlierDate, getLaterDate } from './date.helpers';

export function getActiveTimelineItem<
  T extends IGapV3TimelineItemDto | IFullV3TimelineItemDto | IBooleanV3TimelineItemDto,
>(timeline: T[]): T | undefined {
  return getTimelineItemAtDate(timeline, new Date());
}

/**
 * Get active timeline items for specific date
 * @param timeline
 * @param date - date to check. null or undefined means beginning of time
 */
export function getTimelineItemAtDate<
  T extends IGapV3TimelineItemDto | IFullV3TimelineItemDto | IBooleanV3TimelineItemDto,
>(timeline: T[], date: Date | null | undefined): T | undefined {
  if (!Array.isArray(timeline)) {
    return undefined;
  }

  const normalizedDate = date ?? undefined;

  return timeline.find((item) => isTimelineItemActive(item, normalizedDate));
}

export function getActiveTimelineItems<T extends IOverlapV3TimelineItemDto>(
  timeline: T[],
): T[] | undefined {
  return getTimelineItemsAtDate(timeline, new Date());
}

/**
 * Check if timeline item is active
 * @param timeline
 * @param date - date to check. null or undefined means beginning of time
 */
export function getTimelineItemsAtDate<T extends IOverlapV3TimelineItemDto>(
  timeline: T[],
  date: Date | null | undefined,
): T[] | undefined {
  if (!Array.isArray(timeline)) {
    return undefined;
  }

  const normalizedDate = date ?? undefined;

  return timeline.filter((item) => isTimelineItemActive(item, normalizedDate));
}

/**
 * Get timeline items that start after a specific date.
 * @param timeline An array of timeline items.
 * @param date The date to compare against. Defaults to the current date.
 * @returns An array of timeline items that start after the specified date, or undefined if the input is not an array.
 */
export function filterTimelineItemsAfterDate<T extends TimelineV3ItemDto>(
  timeline: T[],
  date = new Date(),
): T[] | undefined {
  if (!Array.isArray(timeline)) {
    return undefined;
  }
  return timeline.filter((item) => item.from && item.from > date);
}

/**
 * Find the timeline item with the earliest 'from' date after a specific date.
 * @param timeline An array of timeline items.
 * @param date The date to compare against. Defaults to the current date.
 * @returns The timeline item with the earliest 'from' date after the specified date, or undefined if the input is not an array or no item is found.
 */
export function findTimelineItemAfterDate<T extends TimelineV3ItemDto>(
  timeline: T[],
  date = new Date(),
): T | undefined {
  // Check if the input is an array
  if (!Array.isArray(timeline)) {
    return undefined; // Return undefined if the input is not an array
  }

  // Sort the timeline by 'from' date in ascending order
  const sortedTimeline = [...timeline].sort((a, b) => {
    const aFrom = a.from ? a.from.getTime() : Infinity; // Get the 'from' time of item a, or Infinity if 'from' is null/undefined
    const bFrom = b.from ? b.from.getTime() : Infinity; // Get the 'from' time of item b, or Infinity if 'from' is null/undefined
    return aFrom - bFrom; // Compare the 'from' times to sort the timeline
  });

  // Find the first item with a 'from' date strictly greater than the given date
  return sortedTimeline.find((item) => item.from && item.from > date); // Return the first item with a 'from' date after the given date
}

/**
 * Check if timeline item is active
 * @param item
 * @param date - date to check. null or undefined means beginning of time
 */
export function isTimelineItemActive(
  item: TimelineV3ItemDto,
  date: Date | null | undefined,
): boolean {
  const itemFrom = item.from ?? undefined;
  const itemTo = item.to ?? undefined;
  const normalizedDate = date ?? undefined;

  // if item starts from beginning of time it is always active
  if (itemFrom === undefined && normalizedDate === undefined) {
    return true;
  }

  return (
    (itemFrom === undefined || (normalizedDate !== undefined && itemFrom <= normalizedDate)) &&
    (itemTo === undefined || (normalizedDate !== undefined && itemTo >= normalizedDate))
  );
}

export function getActiveAndFutureTimelineItems<T extends IOverlapV3TimelineItemDto>(
  timeline: T[],
): T[] | undefined {
  if (!Array.isArray(timeline)) {
    return undefined;
  }
  return timeline.filter((item) => isTimelineItemActiveOrFuture(item, new Date()));
}

/**
 * Check if timeline item is active now or in future
 * @param item
 * @param date - date to check. null or undefined means beginning of time
 */
export function isTimelineItemActiveOrFuture(
  item: TimelineV3ItemDto,
  date: Date | null | undefined,
): boolean {
  return isTimelineItemFuture(item, date) || isTimelineItemActive(item, date);
}

/**
 * Check if timeline item is in the future
 * @param item
 * @param date - date to check. null or undefined means beginning of time
 */
export function isTimelineItemFuture(
  item: TimelineV3ItemDto,
  date: Date | null | undefined,
): boolean {
  const normalizedDate = date ? new Date(date.toISOString().substring(0, 10)) : undefined;
  return item.from !== undefined && normalizedDate !== undefined && item.from > normalizedDate;
}

/**
 * Get timeline items that were active at any point within the specified period
 * @param timeline
 * @param from - period start. null or undefined means beginning of time
 * @param to - period end. null or undefined means end of time
 */
export function getTimelineItemsActiveInPeriod<T extends TimelineV3ItemDto>(
  timeline: T[],
  from: Date | null | undefined,
  to: Date | null | undefined,
): T[] | undefined {
  if (!Array.isArray(timeline)) {
    return undefined;
  }

  const normalizedFrom = from ?? undefined;
  const normalizedTo = to ?? undefined;

  if (normalizedFrom === undefined && normalizedTo === undefined) {
    return timeline;
  }

  return timeline.filter((item) => {
    return isTimelineItemActiveAtAnyPointInPeriod(item, normalizedFrom, normalizedTo);
  });
}

/**
 * Get timeline items that cover the entire specified period
 * @param timeline
 * @param from - period start. null or undefined means beginning of time
 * @param to - period end. null or undefined means end of time
 */
export function getTimelineItemsCoveringPeriod<T extends TimelineV3ItemDto>(
  timeline: T[],
  from: Date | null | undefined,
  to: Date | null | undefined,
): T[] | undefined {
  if (!Array.isArray(timeline)) {
    return undefined;
  }

  const normalizedFrom = from ?? undefined;
  const normalizedTo = to ?? undefined;
  const checkDate = normalizedTo ?? normalizedFrom;

  return timeline.filter((item) => {
    return isTimelineItemCoveringPeriod(item, normalizedFrom, checkDate);
  });
}

/**
 * Check if timeline item was active at any time in the period
 * @param item
 * @param from - period start. null or undefined means beginning of time
 * @param to - period end. null or undefined means end of time
 */
export function isTimelineItemActiveAtAnyPointInPeriod(
  item: TimelineV3ItemDto,
  from: Date | null | undefined,
  to: Date | null | undefined,
): boolean {
  const normalizedFrom = from ?? undefined;
  const normalizedTo = to ?? undefined;

  if (
    (normalizedFrom === undefined && normalizedTo === undefined) ||
    (item.from === undefined && item.to === undefined)
  ) {
    return true;
  }

  return (
    (item.from === undefined || !normalizedTo || item.from <= normalizedTo) &&
    (item.to === undefined || !normalizedFrom || item.to >= normalizedFrom)
  );
}

/**
 * Check if timeline item covers the entire period
 * @param item
 * @param from - period start. null or undefined means beginning of time
 * @param to - period end. null or undefined means end of time
 */
export function isTimelineItemCoveringPeriod(
  item: TimelineV3ItemDto,
  from: Date | null | undefined,
  to: Date | null | undefined,
): boolean {
  const normalizedFrom = from ?? undefined;
  const normalizedTo = to ?? undefined;

  if (normalizedFrom === undefined && normalizedTo === undefined) {
    return false;
  }

  const itemFrom = item.from ?? undefined;
  const itemTo = item.to ?? undefined;

  const startsBeforeOrAt =
    itemFrom === undefined || (normalizedFrom !== undefined && itemFrom <= normalizedFrom);
  const endsAfterOrAt =
    itemTo === undefined || (normalizedTo !== undefined && itemTo >= normalizedTo);

  return startsBeforeOrAt && endsAfterOrAt;
}

/**
 * Get the overlap period between two periods
 * @param periodA
 * @param periodB
 * @returns The overlap period, or null if there is no overlap
 */
export function getOverlapPeriod(
  periodA: IFullV3TimelineItemDto,
  periodB: IFullV3TimelineItemDto,
): IFullV3TimelineItemDto | null {
  const from = getLaterDate(periodA.from, periodB.from);
  const to = getEarlierDate(periodA.to, periodB.to);

  if (from && to && from?.getTime() > to?.getTime()) {
    return null;
  }

  return { from, to };
}

type TimelineV3ItemDto =
  | IGapV3TimelineItemDto
  | IFullV3TimelineItemDto
  | IOverlapV3TimelineItemDto
  | IBooleanV3TimelineItemDto;
